import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Chip,
    Input
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook,
    faCheckCircle,
    faTimes,
    faRefresh,
    faUndo,
    faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import Alerty from '../../common/Alerty';

const JobCardGLPosting = () => {
    const token = localStorage.getItem('authToken') || '';

    // State
    const [activeTab, setActiveTab] = useState('unposted');
    const [unpostedCards, setUnpostedCards] = useState([]);
    const [postedBatches, setPostedBatches] = useState([]);
    const [selectedCardIds, setSelectedCardIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertColor, setAlertColor] = useState('red');
    const [currentPage, setCurrentPage] = useState(0);

    const showAlert = (message, color = 'red') => {
        setAlertMessage(message);
        setAlertColor(color);
        setAlertOpen(true);
    };
    const [pageSize] = useState(20);

    // Filter states for Unposted tab
    const [unpostedFilters, setUnpostedFilters] = useState({
        ownerCode: '',
        plateNo: '',
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: '',
        serviceLocation: ''
    });

    // Filter states for Posted Batches tab
    const [postedFilters, setPostedFilters] = useState({
        batchId: '',
        startDate: '',
        endDate: '',
        postedBy: '',
        postingType: '',
        minAmount: '',
        maxAmount: '',
        reversed: ''
    });

    const [showUnpostedFilters, setShowUnpostedFilters] = useState(false);
    const [showPostedFilters, setShowPostedFilters] = useState(false);

    // Modals
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showReverseModal, setShowReverseModal] = useState(false);
    const [postingResults, setPostingResults] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [reversalNotes, setReversalNotes] = useState('');

    // Fetch unposted job cards (invoiced but not yet posted to GL)
    const fetchUnpostedCards = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${rootUrl}/api/job-cards/gl/unposted`;
            const params = [];

            if (unpostedFilters.ownerCode) params.push(`ownerCode=${unpostedFilters.ownerCode}`);
            if (unpostedFilters.plateNo) params.push(`plateNo=${unpostedFilters.plateNo}`);
            if (unpostedFilters.startDate) params.push(`startDate=${unpostedFilters.startDate}`);
            if (unpostedFilters.endDate) params.push(`endDate=${unpostedFilters.endDate}`);
            if (unpostedFilters.minAmount) params.push(`minAmount=${unpostedFilters.minAmount}`);
            if (unpostedFilters.maxAmount) params.push(`maxAmount=${unpostedFilters.maxAmount}`);
            if (unpostedFilters.serviceLocation) params.push(`serviceLocation=${unpostedFilters.serviceLocation}`);

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setUnpostedCards(response.data.data || []);
                setSelectedCardIds(new Set());
            }
        } catch (err) {
            console.error('Error fetching invoiced job cards not posted to GL:', err);
            showAlert('Failed to fetch job card invoices not posted to GL', 'red');
        } finally {
            setLoading(false);
        }
    }, [token, unpostedFilters]);

    // Fetch posted batches
    const fetchPostedBatches = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${rootUrl}/api/job-cards/gl/postings?limit=50&offset=${currentPage * pageSize}`;
            
            if (postedFilters.batchId) url += `&batchId=${postedFilters.batchId}`;
            if (postedFilters.startDate) url += `&startDate=${postedFilters.startDate}`;
            if (postedFilters.endDate) url += `&endDate=${postedFilters.endDate}`;
            if (postedFilters.postedBy) url += `&postedBy=${postedFilters.postedBy}`;
            if (postedFilters.postingType) url += `&postingType=${postedFilters.postingType}`;
            if (postedFilters.minAmount) url += `&minAmount=${postedFilters.minAmount}`;
            if (postedFilters.maxAmount) url += `&maxAmount=${postedFilters.maxAmount}`;
            if (postedFilters.reversed !== '') url += `&reversed=${postedFilters.reversed}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setPostedBatches(response.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching posted batches:', err);
            showAlert('Failed to fetch GL posting history', 'red');
        } finally {
            setLoading(false);
        }
    }, [token, currentPage, pageSize, postedFilters]);

    useEffect(() => {
        if (activeTab === 'unposted') {
            fetchUnpostedCards();
        } else {
            fetchPostedBatches();
        }
    }, [activeTab, fetchUnpostedCards, fetchPostedBatches]);

    // Handle card selection
    const handleSelectCard = (cardId) => {
        const newSelection = new Set(selectedCardIds);
        if (newSelection.has(cardId)) {
            newSelection.delete(cardId);
        } else {
            newSelection.add(cardId);
        }
        setSelectedCardIds(newSelection);
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedCardIds.size === unpostedCards.length) {
            setSelectedCardIds(new Set());
        } else {
            setSelectedCardIds(new Set(unpostedCards.map(card => card.id)));
        }
    };

    // Handle batch posting
    const handleBatchPost = async () => {
        if (selectedCardIds.size === 0) {
            showAlert('Please select at least one job card', 'yellow');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(
                `${rootUrl}/api/job-cards/gl/post-batch`,
                { job_card_ids: Array.from(selectedCardIds) },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPostingResults({
                    success: true,
                    posting_batch_id: response.data.posting_batch_id,
                    invoice_count: response.data.invoice_count,
                    total_amount: response.data.total_amount
                });
                setShowConfirmModal(false);
                setShowResultsModal(true);
                setSelectedCardIds(new Set());
                // Refresh both lists
                await fetchUnpostedCards();
                await fetchPostedBatches();
            } else {
                showAlert(response.data.message || 'Posting failed', 'red');
            }
        } catch (err) {
            console.error('Error posting to GL:', err);
            showAlert(err.response?.data?.message || 'Failed to post to GL', 'red');
        } finally {
            setLoading(false);
        }
    };

    // Fetch posting details
    const handleViewDetails = async (batchId) => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${rootUrl}/api/job-cards/gl/postings/${batchId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setSelectedBatch(response.data);
                setShowDetailsModal(true);
            }
        } catch (err) {
            console.error('Error fetching posting details:', err);
            showAlert('Failed to fetch posting details', 'red');
        } finally {
            setLoading(false);
        }
    };

    // Handle reversal
    const handleReverse = async () => {
        if (!selectedBatch) return;

        try {
            setLoading(true);
            const response = await axios.post(
                `${rootUrl}/api/job-cards/gl/postings/${selectedBatch.posting.posting_batch_id}/reverse`,
                { reversal_notes: reversalNotes },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                showAlert(`GL posting reversed successfully. Reversal ref: ${response.data.reversal_ref}`, 'green');
                setShowReverseModal(false);
                setShowDetailsModal(false);
                setSelectedBatch(null);
                setReversalNotes('');
                await fetchPostedBatches();
            } else {
                showAlert(response.data.message || 'Reversal failed', 'red');
            }
        } catch (err) {
            console.error('Error reversing GL posting:', err);
            showAlert(err.response?.data?.message || 'Failed to reverse GL posting', 'red');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        });
    };

    // Clear filters
    const clearUnpostedFilters = () => {
        setUnpostedFilters({
            ownerCode: '',
            plateNo: '',
            startDate: '',
            endDate: '',
            minAmount: '',
            maxAmount: '',
            serviceLocation: ''
        });
    };

    const clearPostedFilters = () => {
        setPostedFilters({
            batchId: '',
            startDate: '',
            endDate: '',
            postedBy: '',
            postingType: '',
            minAmount: '',
            maxAmount: '',
            reversed: ''
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">GL Job Card Posting</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Manage GL postings for job card invoices
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <Alerty
                    open={alertOpen}
                    setOpen={setAlertOpen}
                    color={alertColor}
                    message={alertMessage}
                />

                <Card className="bg-white rounded-2xl shadow-xl">
                    <CardBody className="p-6">
                        {/* Tab Strip */}
                        <div className="flex border-b border-gray-200 mb-6">
                            <button
                                onClick={() => {
                                    setActiveTab('unposted');
                                    setCurrentPage(0);
                                }}
                                className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'unposted'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faBook} className="mr-2" />
                                Invoiced - Not Posted
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('posted');
                                    setCurrentPage(0);
                                }}
                                className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'posted'
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-600'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                Posted Batches
                            </button>
                        </div>

                        {/* Unposted Tab */}
                        {activeTab === 'unposted' && (
                            <div className="space-y-4">
                                {/* Filter Section */}
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <button
                                            onClick={() => setShowUnpostedFilters(!showUnpostedFilters)}
                                            className="flex items-center text-gray-700 font-semibold hover:text-[#FF5733]"
                                        >
                                            <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                            {showUnpostedFilters ? 'Hide Filters' : 'Show Filters'}
                                        </button>
                                        {(unpostedFilters.ownerCode || unpostedFilters.plateNo || unpostedFilters.startDate || 
                                          unpostedFilters.endDate || unpostedFilters.minAmount || unpostedFilters.maxAmount ||
                                          unpostedFilters.serviceLocation) && (
                                            <button
                                                onClick={clearUnpostedFilters}
                                                className="text-sm text-red-600 hover:text-red-800"
                                            >
                                                Clear All Filters
                                            </button>
                                        )}
                                    </div>

                                    {showUnpostedFilters && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <Input
                                                label="Owner Code"
                                                value={unpostedFilters.ownerCode}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, ownerCode: e.target.value })}
                                                placeholder="Filter by owner"
                                            />
                                            <Input
                                                label="Plate Number"
                                                value={unpostedFilters.plateNo}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, plateNo: e.target.value })}
                                                placeholder="Search plate"
                                            />
                                            <Input
                                                label="Start Date"
                                                type="date"
                                                value={unpostedFilters.startDate}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, startDate: e.target.value })}
                                            />
                                            <Input
                                                label="End Date"
                                                type="date"
                                                value={unpostedFilters.endDate}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, endDate: e.target.value })}
                                            />
                                            <Input
                                                label="Min Amount (KES)"
                                                type="number"
                                                value={unpostedFilters.minAmount}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, minAmount: e.target.value })}
                                                placeholder="0"
                                            />
                                            <Input
                                                label="Max Amount (KES)"
                                                type="number"
                                                value={unpostedFilters.maxAmount}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, maxAmount: e.target.value })}
                                                placeholder="999999"
                                            />
                                            <Input
                                                label="Service Location"
                                                value={unpostedFilters.serviceLocation}
                                                onChange={(e) => setUnpostedFilters({ ...unpostedFilters, serviceLocation: e.target.value })}
                                                placeholder="GARAGE/SHOP"
                                            />
                                            <div className="flex items-end">
                                                <Button
                                                    onClick={fetchUnpostedCards}
                                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white w-full"
                                                    disabled={loading}
                                                >
                                                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                                    Apply Filters
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between items-center mb-4">
                                    <Typography variant="h6">
                                        {unpostedCards.length} invoiced job card(s) awaiting GL posting
                                    </Typography>
                                    {unpostedCards.length > 0 && (
                                        <Button
                                            onClick={() => setShowConfirmModal(true)}
                                            disabled={selectedCardIds.size === 0 || loading}
                                            className="bg-[#FF5733] hover:bg-[#e64520] text-white"
                                        >
                                            Post to GL ({selectedCardIds.size})
                                        </Button>
                                    )}
                                </div>

                                {unpostedCards.length === 0 ? (
                                    <Typography className="text-center text-gray-500 py-8">
                                        No invoiced job cards awaiting GL posting
                                    </Typography>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCardIds.size === unpostedCards.length && unpostedCards.length > 0}
                                                            onChange={handleSelectAll}
                                                        />
                                                    </th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Job Card No</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Plate No</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Owner</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Invoice Date</th>
                                                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {unpostedCards.map((card) => (
                                                    <tr key={card.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCardIds.has(card.id)}
                                                                onChange={() => handleSelectCard(card.id)}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 font-mono text-gray-900">{card.job_card_no}</td>
                                                        <td className="px-4 py-3 text-gray-700">{card.plate_no}</td>
                                                        <td className="px-4 py-3 text-gray-700">{card.owner_code}</td>
                                                        <td className="px-4 py-3 text-gray-700">{formatDate(card.invoice_date)}</td>
                                                        <td className="px-4 py-3 text-right font-semibold">
                                                            {formatCurrency(card.total_cost)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Posted Tab */}
                        {activeTab === 'posted' && (
                            <div className="space-y-4">
                                {/* Filter Section */}
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <button
                                            onClick={() => setShowPostedFilters(!showPostedFilters)}
                                            className="flex items-center text-gray-700 font-semibold hover:text-[#FF5733]"
                                        >
                                            <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                            {showPostedFilters ? 'Hide Filters' : 'Show Filters'}
                                        </button>
                                        {(postedFilters.batchId || postedFilters.startDate || postedFilters.endDate || 
                                          postedFilters.postedBy || postedFilters.postingType || postedFilters.minAmount || 
                                          postedFilters.maxAmount || postedFilters.reversed !== '') && (
                                            <button
                                                onClick={clearPostedFilters}
                                                className="text-sm text-red-600 hover:text-red-800"
                                            >
                                                Clear All Filters
                                            </button>
                                        )}
                                    </div>

                                    {showPostedFilters && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <Input
                                                label="Batch ID"
                                                value={postedFilters.batchId}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, batchId: e.target.value })}
                                                placeholder="Search batch ID"
                                            />
                                            <Input
                                                label="Posted By"
                                                value={postedFilters.postedBy}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, postedBy: e.target.value })}
                                                placeholder="Username"
                                            />
                                            <Input
                                                label="Start Date"
                                                type="date"
                                                value={postedFilters.startDate}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, startDate: e.target.value })}
                                            />
                                            <Input
                                                label="End Date"
                                                type="date"
                                                value={postedFilters.endDate}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, endDate: e.target.value })}
                                            />
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Posting Type
                                                </label>
                                                <select
                                                    value={postedFilters.postingType}
                                                    onChange={(e) => setPostedFilters({ ...postedFilters, postingType: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                >
                                                    <option value="">All Types</option>
                                                    <option value="BATCH">Batch</option>
                                                    <option value="INDIVIDUAL">Individual</option>
                                                    <option value="REVERSAL">Reversal</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Status
                                                </label>
                                                <select
                                                    value={postedFilters.reversed}
                                                    onChange={(e) => setPostedFilters({ ...postedFilters, reversed: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                >
                                                    <option value="">All Statuses</option>
                                                    <option value="false">Posted</option>
                                                    <option value="true">Reversed</option>
                                                </select>
                                            </div>
                                            <Input
                                                label="Min Amount (KES)"
                                                type="number"
                                                value={postedFilters.minAmount}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, minAmount: e.target.value })}
                                                placeholder="0"
                                            />
                                            <Input
                                                label="Max Amount (KES)"
                                                type="number"
                                                value={postedFilters.maxAmount}
                                                onChange={(e) => setPostedFilters({ ...postedFilters, maxAmount: e.target.value })}
                                                placeholder="999999"
                                            />
                                            <div className="flex items-end">
                                                <Button
                                                    onClick={fetchPostedBatches}
                                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white w-full"
                                                    disabled={loading}
                                                >
                                                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                                    Apply Filters
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between items-center mb-4">
                                    <Typography variant="h6">
                                        {postedBatches.length} posting(s)
                                    </Typography>
                                    <Button
                                        onClick={() => fetchPostedBatches()}
                                        disabled={loading}
                                        className="bg-slate-100 hover:bg-slate-200 text-slate-700"
                                    >
                                        <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                                        Refresh
                                    </Button>
                                </div>

                                {postedBatches.length === 0 ? (
                                    <Typography className="text-center text-gray-500 py-8">
                                        No GL postings found
                                    </Typography>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Batch ID</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Count</th>
                                                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Posted Date</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Posted By</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                                                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {postedBatches.map((batch) => (
                                                    <tr key={batch.posting_batch_id} className="border-b border-gray-200 hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-mono text-xs text-gray-900">{batch.posting_batch_id}</td>
                                                        <td className="px-4 py-3 text-center text-gray-700">{batch.invoice_count}</td>
                                                        <td className="px-4 py-3 text-right font-semibold">
                                                            {formatCurrency(batch.total_amount)}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-700">{formatDate(batch.posting_date)}</td>
                                                        <td className="px-4 py-3 text-gray-700">{batch.posted_by}</td>
                                                        <td className="px-4 py-3">
                                                            <Chip
                                                                value={batch.reversed ? 'REVERSED' : 'POSTED'}
                                                                color={batch.reversed ? 'orange' : 'green'}
                                                                className="w-fit"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                onClick={() => handleViewDetails(batch.posting_batch_id)}
                                                                className="text-blue-600 hover:text-blue-800 font-semibold text-xs"
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Confirm Posting Modal */}
            <Dialog open={showConfirmModal} size="md">
                <DialogHeader>Confirm GL Posting</DialogHeader>
                <DialogBody>
                    <Typography>
                        You are about to post <strong>{selectedCardIds.size}</strong> job card invoice(s) to the General Ledger.
                    </Typography>
                    <Typography className="mt-4 text-gray-600">
                        Once posted, these invoices will be locked and can only be reversed via a separate GL reversal entry.
                        This action cannot be undone directly.
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={() => setShowConfirmModal(false)}
                        className="mr-2"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBatchPost}
                        disabled={loading}
                        className="bg-[#FF5733] hover:bg-[#e64520] text-white"
                    >
                        {loading ? 'Posting...' : 'Confirm & Post'}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Posting Results Modal */}
            <Dialog open={showResultsModal} size="md">
                <DialogHeader>Posting Complete</DialogHeader>
                <DialogBody>
                    {postingResults?.success ? (
                        <div className="space-y-4">
                            <div className="flex items-center text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-2xl mr-3" />
                                <Typography className="font-semibold">GL posting successful</Typography>
                            </div>
                            <div className="bg-green-50 p-4 rounded">
                                <p className="text-sm text-gray-700">
                                    <strong>Batch ID:</strong> {postingResults.posting_batch_id}
                                </p>
                                <p className="text-sm text-gray-700 mt-2">
                                    <strong>Invoices Posted:</strong> {postingResults.invoice_count}
                                </p>
                                <p className="text-sm text-gray-700 mt-2">
                                    <strong>Total Amount:</strong> {formatCurrency(postingResults.total_amount)}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-red-600">Posting failed. Please try again.</div>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        onClick={() => setShowResultsModal(false)}
                        className="bg-[#FF5733] hover:bg-[#e64520] text-white"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Posting Details Modal */}
            <Dialog open={showDetailsModal} size="lg">
                <DialogHeader>
                    <div className="flex justify-between w-full items-center">
                        <span>GL Posting Details</span>
                        <button
                            onClick={() => setShowDetailsModal(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </DialogHeader>
                {selectedBatch && (
                    <DialogBody>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography className="text-xs font-semibold text-gray-600">Batch ID</Typography>
                                    <Typography className="font-mono text-sm">{selectedBatch.posting.posting_batch_id}</Typography>
                                </div>
                                <div>
                                    <Typography className="text-xs font-semibold text-gray-600">Posted By</Typography>
                                    <Typography className="text-sm">{selectedBatch.posting.posted_by}</Typography>
                                </div>
                                <div>
                                    <Typography className="text-xs font-semibold text-gray-600">Posted Date</Typography>
                                    <Typography className="text-sm">{formatDate(selectedBatch.posting.posting_date)}</Typography>
                                </div>
                                <div>
                                    <Typography className="text-xs font-semibold text-gray-600">Total Amount</Typography>
                                    <Typography className="text-sm font-semibold">
                                        {formatCurrency(selectedBatch.posting.total_amount)}
                                    </Typography>
                                </div>
                            </div>

                            <div className="mt-6">
                                <Typography className="text-sm font-semibold mb-3">GL Entries</Typography>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs border border-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-2 text-left border-b">Account Code</th>
                                                <th className="px-2 py-2 text-left border-b">Description</th>
                                                <th className="px-2 py-2 text-right border-b">Debit</th>
                                                <th className="px-2 py-2 text-right border-b">Credit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedBatch.details?.map((detail, idx) => (
                                                <tr key={idx} className="border-b">
                                                    <td className="px-2 py-2 font-mono">{detail.account_code}</td>
                                                    <td className="px-2 py-2 text-gray-700">{detail.line_description}</td>
                                                    <td className="px-2 py-2 text-right">
                                                        {detail.debit_amount > 0 ? formatCurrency(detail.debit_amount) : '-'}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">
                                                        {detail.credit_amount > 0 ? formatCurrency(detail.credit_amount) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </DialogBody>
                )}
                <DialogFooter>
                    {selectedBatch && !selectedBatch.posting.reversed && (
                        <Button
                            onClick={() => {
                                setShowDetailsModal(false);
                                setShowReverseModal(true);
                            }}
                            className="bg-orange-500 hover:bg-orange-600 text-white mr-2"
                        >
                            <FontAwesomeIcon icon={faUndo} className="mr-2" />
                            Reverse Posting
                        </Button>
                    )}
                    <Button
                        variant="text"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Reversal Modal */}
            <Dialog open={showReverseModal} size="md">
                <DialogHeader>Reverse GL Posting</DialogHeader>
                <DialogBody>
                    <Typography className="mb-4">
                        Enter optional notes for this reversal:
                    </Typography>
                    <Input
                        type="text"
                        label="Reversal Notes (Optional)"
                        value={reversalNotes}
                        onChange={(e) => setReversalNotes(e.target.value)}
                        placeholder="e.g., Incorrect invoice amount, duplicate posting, etc."
                    />
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={() => {
                            setShowReverseModal(false);
                            setReversalNotes('');
                        }}
                        className="mr-2"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReverse}
                        disabled={loading}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                        {loading ? 'Reversing...' : 'Confirm Reversal'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default JobCardGLPosting;
